import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import ComicViewer from '../components/stories/ComicViewer';
import api, { resolveMediaUrl } from '../api/api';
import { storyToComicCard } from '../utils/storyComicFormat';
import './StoriesPage.css';

/** 기존 정적 홍보툰(샘플) — API에 동일 형식으로 올리면 이 목록은 제거 가능 */
const LEGACY_COMIC_CARDS = [
  {
    id: 'short_comic_1',
    title: '외상센터 이야기 #1',
    description: '골든타임의 중요성',
    thumbnail: '/short_comics/short_comic_1/1758884457525.png',
    images: [
      '/short_comics/short_comic_1/1758884457525.png',
      '/short_comics/short_comic_1/1758884457583.png',
      '/short_comics/short_comic_1/1758884457630.png',
      '/short_comics/short_comic_1/1758884457676.png',
      '/short_comics/short_comic_1/1758884457731.png',
      '/short_comics/short_comic_1/1758884457779.png',
      '/short_comics/short_comic_1/1758884457816.png',
      '/short_comics/short_comic_1/1758884457855.png',
      '/short_comics/short_comic_1/1758884457895.png',
    ],
  },
  {
    id: 'short_comic_2',
    title: '외상센터 이야기 #2',
    description: '응급 대응의 중요성',
    thumbnail: '/short_comics/short_comic_2/Comic2_1.png',
    images: [
      '/short_comics/short_comic_2/Comic2_1.png',
      '/short_comics/short_comic_2/Comic2_2.png',
      '/short_comics/short_comic_2/Comic2_3.png',
      '/short_comics/short_comic_2/Comic2_4.png',
      '/short_comics/short_comic_2/Comic2_5.png',
      '/short_comics/short_comic_2/Comic2_6.png',
      '/short_comics/short_comic_2/Comic2_7.png',
      '/short_comics/short_comic_2/Comic2_8.png',
      '/short_comics/short_comic_2/Comic2_9.png',
    ],
  },
];

/** 본문에서 인스타그램 게시물 permalink 추출 */
function getInstagramPermalink(text) {
  if (!text || !String(text).trim()) return null;
  const u = String(text).trim();
  const full = u.match(
    /(?:https?:\/\/)?(?:www\.)?instagram\.com\/(p|reel|reels|tv)\/([A-Za-z0-9_-]+)/i
  );
  if (full) {
    const segment = full[1].toLowerCase() === 'reels' ? 'reel' : full[1].toLowerCase();
    return `https://www.instagram.com/${segment}/${full[2]}/`;
  }
  if (/^[A-Za-z0-9_-]+$/.test(u)) return `https://www.instagram.com/p/${u}/`;
  return null;
}

/** 인스타 공식 embed (blockquote + embed.js) */
function InstagramEmbed({ permalink }) {
  useEffect(() => {
    if (!permalink) return undefined;
    const process = () => {
      if (window.instgrm?.Embeds?.process) window.instgrm.Embeds.process();
    };
    const existing = document.querySelector('script[src*="instagram.com/embed.js"]');
    if (existing) {
      process();
      return undefined;
    }
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.instagram.com/embed.js';
    script.onload = process;
    document.body.appendChild(script);
    return undefined;
  }, [permalink]);

  if (!permalink) return null;
  return (
    <blockquote
      className="instagram-media"
      data-instgrm-permalink={permalink}
      data-instgrm-version="14"
      style={{
        background: '#FFF',
        border: 0,
        margin: 0,
        maxWidth: '540px',
        width: '100%',
      }}
    />
  );
}

/** 본문 텍스트에서 유튜브 embed URL 추출 */
function getYoutubeEmbedUrl(text) {
  if (!text || !String(text).trim()) return null;
  const u = String(text).trim();
  const watch = u.match(/youtube\.com\/watch\?v=([^&\s#]+)/);
  if (watch) return `https://www.youtube.com/embed/${watch[1]}`;
  const short = u.match(/youtu\.be\/([^?\s#]+)/);
  if (short) return `https://www.youtube.com/embed/${short[1]}`;
  const embed = u.match(/youtube\.com\/embed\/([^?\s#]+)/);
  if (embed) return `https://www.youtube.com/embed/${embed[1]}`;
  if (/^[a-zA-Z0-9_-]{11}$/.test(u)) return `https://www.youtube.com/embed/${u}`;
  return null;
}

const StoriesPage = () => {
  const { t } = useTranslation();

  // 기본 탭은 'all'이지만, 실제 표시용은 'comic'으로 매핑
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedComic, setSelectedComic] = useState(null);
  const [comics, setComics] = useState([]);
  const [youtubeStories, setYoutubeStories] = useState([]);
  const [youtubeLoading, setYoutubeLoading] = useState(false);
  const [instagramStories, setInstagramStories] = useState([]);
  const [instagramLoading, setInstagramLoading] = useState(false);
  const [comicLoading, setComicLoading] = useState(false);

  const effectiveCategory = useMemo(() => {
    return activeCategory === 'all' ? 'comic' : activeCategory;
  }, [activeCategory]);

  /** 홍보툰: API(category=comic) + 기존 정적 샘플 — 카드/뷰어 형식 동일 */
  useEffect(() => {
    if (effectiveCategory !== 'comic') return undefined;
    let cancelled = false;
    setComicLoading(true);
    api
      .getStories('comic', 1, 50)
      .then((res) => {
        if (cancelled) return;
        const fromApi = (res.stories || [])
          .map((row) => storyToComicCard(row))
          .filter((c) => c.images.length > 0);
        setComics([...fromApi, ...LEGACY_COMIC_CARDS]);
      })
      .catch(() => {
        if (!cancelled) setComics([...LEGACY_COMIC_CARDS]);
      })
      .finally(() => {
        if (!cancelled) setComicLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveCategory]);

  useEffect(() => {
    if (effectiveCategory !== 'youtube') return undefined;
    let cancelled = false;
    setYoutubeLoading(true);
    api
      .getStories('youtube', 1, 50)
      .then((res) => {
        if (!cancelled) setYoutubeStories(res.stories || []);
      })
      .catch(() => {
        if (!cancelled) setYoutubeStories([]);
      })
      .finally(() => {
        if (!cancelled) setYoutubeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveCategory]);

  useEffect(() => {
    if (effectiveCategory !== 'instagram') return undefined;
    let cancelled = false;
    setInstagramLoading(true);
    api
      .getStories('instagram', 1, 50)
      .then((res) => {
        if (!cancelled) setInstagramStories(res.stories || []);
      })
      .catch(() => {
        if (!cancelled) setInstagramStories([]);
      })
      .finally(() => {
        if (!cancelled) setInstagramLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveCategory]);

  const handleComicClick = (comic) => setSelectedComic(comic);
  const handleCloseComic = () => setSelectedComic(null);

  const categories = [
    { id: 'all', label: t('stories.category.all') },
    { id: 'comic', label: t('stories.category.comic') },   // 홍보툰
    { id: 'youtube', label: t('stories.category.youtube') },
    { id: 'instagram', label: t('stories.category.instagram') },
    { id: 'donor', label: t('stories.category.donor') },
    { id: 'column', label: t('stories.category.column') },
    { id: 'press', label: t('stories.category.press') },
  ];

  return (
    <>
      <Header showPageHero={false} />

      <main>
        <section className="stories-content">
          <div className="container">
            <h1 className="stories-page-title">{t('stories.title')}</h1>

            {/* 탭 */}
            <div className="category-filter">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  className={activeCategory === cat.id ? 'active' : ''}
                  onClick={() => {
                    setActiveCategory(cat.id);
                    setSelectedComic(null);
                  }}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* ✅ 홍보툰(웹툰) 목록: API·관리자 업로드와 동일 카드 형식 */}
            {effectiveCategory === 'comic' && !selectedComic && comicLoading && (
              <div className="story-empty" role="status">
                {t('stories.loading', '불러오는 중...')}
              </div>
            )}
            {effectiveCategory === 'comic' && !selectedComic && !comicLoading && (
              <div className="comics-grid">
                {comics.map((comic) => (
                  <div
                    key={comic.id}
                    className="comic-card"
                    onClick={() => handleComicClick(comic)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => (e.key === 'Enter' ? handleComicClick(comic) : null)}
                  >
                    <div className="comic-thumbnail">
                      <img src={resolveMediaUrl(comic.thumbnail)} alt={comic.title} />
                      <div className="comic-overlay">
                        <span>{t('stories.read', '읽기')}</span>
                      </div>
                    </div>
                    <div className="comic-card-info">
                      <h3>{comic.title}</h3>
                      <p>{comic.description}</p>
                      <span className="comic-pages">
                        {comic.images.length}{t('stories.pages', '페이지')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ✅ 홍보툰 뷰어 */}
            {effectiveCategory === 'comic' && selectedComic && (
              <div className="comic-viewer-container">
                <button onClick={handleCloseComic} className="back-to-list-btn">
                  ← {t('stories.backToList', '목록으로 돌아가기')}
                </button>
                <ComicViewer
                  comicId={selectedComic.id}
                  images={selectedComic.images}
                  title={selectedComic.title}
                />
              </div>
            )}

            {/* ✅ 유튜브 탭: API 스토리(category=youtube) */}
            {effectiveCategory === 'youtube' && (
              <div className="youtube-stories-section">
                {youtubeLoading && (
                  <div className="story-empty" role="status">
                    {t('stories.loading', '불러오는 중...')}
                  </div>
                )}
                {!youtubeLoading && youtubeStories.length === 0 && (
                  <div className="story-empty" role="status">
                    {t('stories.emptyYoutube', '등록된 영상이 없습니다.')}
                  </div>
                )}
                {!youtubeLoading && youtubeStories.length > 0 && (
                  <div className="youtube-grid">
                    {youtubeStories.map((story) => {
                      const embedUrl = getYoutubeEmbedUrl(story.content);
                      return (
                        <article key={story.id} className="youtube-card">
                          {story.image && (
                            <div className="youtube-card-thumb">
                              <img src={resolveMediaUrl(story.image)} alt="" />
                            </div>
                          )}
                          <h3 className="youtube-card-title">{story.title}</h3>
                          <p className="youtube-card-summary">{story.summary}</p>
                          {embedUrl && (
                            <div className="youtube-embed-wrap">
                              <iframe
                                title={story.title}
                                src={embedUrl}
                                allowFullScreen
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              />
                            </div>
                          )}
                          {!embedUrl && story.content && (
                            <p className="youtube-fallback-text">{story.content}</p>
                          )}
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* 인스타 탭: API 스토리(category=instagram) */}
            {effectiveCategory === 'instagram' && (
              <div className="instagram-stories-section">
                {instagramLoading && (
                  <div className="story-empty" role="status">
                    {t('stories.loading', '불러오는 중...')}
                  </div>
                )}
                {!instagramLoading && instagramStories.length === 0 && (
                  <div className="story-empty" role="status">
                    {t('stories.emptyInstagram', '등록된 게시물이 없습니다.')}
                  </div>
                )}
                {!instagramLoading && instagramStories.length > 0 && (
                  <div className="instagram-grid">
                    {instagramStories.map((story) => {
                      const permalink = getInstagramPermalink(story.content);
                      return (
                        <article key={story.id} className="instagram-card">
                          {story.image && (
                            <div className="instagram-card-thumb">
                              <img src={resolveMediaUrl(story.image)} alt="" />
                            </div>
                          )}
                          <h3 className="instagram-card-title">{story.title}</h3>
                          <p className="instagram-card-summary">{story.summary}</p>
                          {permalink ? (
                            <div className="instagram-embed-wrap">
                              <InstagramEmbed permalink={permalink} />
                            </div>
                          ) : (
                            story.content && (
                              <p className="instagram-fallback-text">
                                {story.content}
                                {String(story.content).startsWith('http') && (
                                  <>
                                    {' '}
                                    <a href={story.content} target="_blank" rel="noopener noreferrer">
                                      {t('stories.openInstagram', '인스타에서 보기')}
                                    </a>
                                  </>
                                )}
                              </p>
                            )
                          )}
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ✅ 기부자·칼럼·언론 등 (홍보툰·유튜브·인스타 제외) */}
            {effectiveCategory !== 'comic' &&
              effectiveCategory !== 'youtube' &&
              effectiveCategory !== 'instagram' && (
              <div className="story-empty" role="status" aria-live="polite">
                {t('stories.empty', '등록된 스토리가 없습니다.')}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default StoriesPage;